import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DataContext } from '../../context/DataProvider';
import Button from '../../components/common/Button';
import { Plus, Trash2, LogOut, Edit2, X, Upload, Users } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { supabase } from '../../supabase';
import CustomDialog from '../../components/common/CustomDialog';

const AdminDashboard = () => {
    const { 
        courses, addCourse, deleteCourse, updateCourse, 
        jobs, addJob, deleteJob, updateJob, 
        teamMembers, addTeamMember, deleteTeamMember, updateTeamMember,
        projects, addProject, deleteProject, updateProject,
        loading 
    } = useContext(DataContext);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('courses');
    const [editingId, setEditingId] = useState(null);

    // Auth Check
    useEffect(() => {
        if (!localStorage.getItem('isAdmin')) {
            navigate('/admin');
        }
    }, [navigate]);

    // Handle Back Button & Logout Logic
    useEffect(() => {
        // Push a state to trap the back button
        window.history.pushState(null, null, window.location.pathname);

        const handlePopState = (event) => {
            // Prevent default back behavior initially
            const confirmLogout = window.confirm("Do you want to logout from admin panel?");

            if (confirmLogout) {
                localStorage.removeItem('isAdmin');
                // Allow the back navigation to proceed (which effectively goes back from the pushed state)
                // But since we are logged out, we want to go back to the page BEFORE admin dashboard
                navigate(-1); // Go back twice: once for the pushed state, once to exit dashboard
            } else {
                // Push state again to re-trap
                window.history.pushState(null, null, window.location.pathname);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    const handleLogout = () => {
        if (window.confirm("Are you sure you want to logout?")) {
            localStorage.removeItem('isAdmin');
            navigate('/admin');
        }
    };

    // Form States
    const [courseForm, setCourseForm] = useState({ title: '', desc: '', level: 'Beginner', format: 'Video Course', price: '', status: 'Enroll Now', thumbnail_url: '', batch_date: '' });

    // Debug render
    // console.log('Current Form State:', courseForm);

    const [jobForm, setJobForm] = useState({ title: '', type: 'Remote', desc: '' });
    const [teamForm, setTeamForm] = useState({ name: '', role: '', image_url: '' });
    const [projectForm, setProjectForm] = useState({ title: '', category: '', description: '', image_url: '', project_link: '' });
    const [uploading, setUploading] = useState(false);

    // Custom Dialog State
    const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', type: 'success' });

    const resetForms = () => {
        setEditingId(null);
        setCourseForm({ title: '', desc: '', level: 'Beginner', format: 'Video Course', price: '', status: 'Enroll Now', thumbnail_url: '', batch_date: '' });
        setJobForm({ title: '', type: 'Remote', desc: '' });
        setTeamForm({ name: '', role: '', image_url: '' });
        setProjectForm({ title: '', category: '', description: '', image_url: '', project_link: '' });
    };

    const [pendingDelete, setPendingDelete] = useState({ type: '', id: null });

    const closeDialog = () => {
        setDialog({ ...dialog, isOpen: false });
        setPendingDelete({ type: '', id: null });
    };

    const handleConfirmAction = async () => {
        if (!pendingDelete.id) return;

        closeDialog(); // Close confirmation dialog

        let result;
        if (pendingDelete.type === 'course') {
            result = await deleteCourse(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Course Deleted', message: 'The course has been successfully removed.', type: 'success' });
            }
        } else if (pendingDelete.type === 'job') {
            result = await deleteJob(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Job Deleted', message: 'The job position has been successfully removed.', type: 'success' });
            }
        } else if (pendingDelete.type === 'team') {
            result = await deleteTeamMember(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Team Member Removed', message: 'The team member has been successfully removed.', type: 'success' });
            }
        } else if (pendingDelete.type === 'project') {
            result = await deleteProject(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Project Deleted', message: 'The project has been successfully removed.', type: 'success' });
            }
        }

        if (result && !result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        }
    };

    const handleEditCourse = (course) => {
        console.log('Editing course - Incoming Data:', course);
        setEditingId(course.id);
        const newFormState = {
            id: course.id,
            title: course.title || '',
            desc: course.desc || '',
            level: course.level || 'Beginner',
            format: course.format || 'Video Course',
            price: course.price || '',
            status: course.status || 'Enroll Now',
            thumbnail_url: course.thumbnail_url || '',
            batch_date: course.batch_date ? new Date(course.batch_date).toISOString().split('T')[0] : ''
        };
        // Use functional update to ensure we don't depend on stale closures, 
        // essentially replacing user action with this state.
        setCourseForm(newFormState);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleEditJob = (job) => {
        setEditingId(job.id);
        setJobForm(job);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleEditTeamMember = (member) => {
        setEditingId(member.id);
        setTeamForm(member);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleImageUpload = async (e, type = 'course') => {
        try {
            const file = e.target.files[0];
            if (!file) return;

            setUploading(true);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;
            // const bucket = type === 'team' ? 'team-assets' : 'course-assets';

            // Ensure bucket exists or use a shared one. Ideally 'course-assets' can be renamed to 'public-assets' but for now we can reuse it or use a specific folder if needed.
            // Let's stick with 'course-assets' for simplicity or create 'team-assets' if critical.
            // Assuming we use 'course-assets' for all public updates for now to avoid bucket creation overhead unless specified.
            // Actually, let's use 'course-assets' as a general bucket for this project if 'team-assets' doesn't exist.
            
            const { error: uploadError } = await supabase.storage
                .from('course-assets') 
                .upload(filePath, file);

            if (uploadError) {
                throw uploadError;
            }

            const { data } = supabase.storage
                .from('course-assets')
                .getPublicUrl(filePath);

            if (type === 'team') {
                setTeamForm(prev => ({ ...prev, image_url: data.publicUrl }));
            } else if (type === 'project') {
                setProjectForm(prev => ({ ...prev, image_url: data.publicUrl }));
            } else {
                setCourseForm(prev => ({ ...prev, thumbnail_url: data.publicUrl }));
            }
        } catch (error) {
            setDialog({ isOpen: true, title: 'Upload Failed', message: error.message, type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleSubmitCourse = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateCourse(courseForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Course Updated', message: 'The course has been successfully updated.', type: 'success' });
            }
        } else {
            result = await addCourse(courseForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Course Added', message: 'New course has been successfully added.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleSubmitJob = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateJob(jobForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Job Updated', message: 'The job position has been successfully updated.', type: 'success' });
            }
        } else {
            result = await addJob({ ...jobForm, role: jobForm.title });
            if (result.success) {
                setDialog({ isOpen: true, title: 'Job Added', message: 'New job position has been successfully added.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleSubmitTeam = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateTeamMember(teamForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Team Member Updated', message: 'Team member details updated successfully.', type: 'success' });
            }
        } else {
            result = await addTeamMember(teamForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Team Member Added', message: 'New team member added successfully.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleDeleteCourse = (id) => {
        setPendingDelete({ type: 'course', id });
        setDialog({
            isOpen: true,
            title: 'Delete Course',
            message: 'Are you sure you want to delete this course? This action cannot be undone.',
            type: 'confirm'
        });
    };

    const handleDeleteJob = (id) => {
        setPendingDelete({ type: 'job', id });
        setDialog({
            isOpen: true,
            title: 'Delete Job',
            message: 'Are you sure you want to delete this job position? This action cannot be undone.',
            type: 'confirm'
        });
    };

    const handleDeleteTeamMember = (id) => {
        setPendingDelete({ type: 'team', id });
        setDialog({
            isOpen: true,
            title: 'Delete Team Member',
            message: 'Are you sure you want to delete this team member? This action cannot be undone.',
            type: 'confirm'
        });
    };

    const handleEditProject = (project) => {
        setEditingId(project.id);
        setProjectForm(project);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmitProject = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateProject(projectForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Project Updated', message: 'The project has been successfully updated.', type: 'success' });
            }
        } else {
            result = await addProject(projectForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Project Added', message: 'New project added successfully.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleDeleteProject = (id) => {
        setPendingDelete({ type: 'project', id });
        setDialog({
            isOpen: true,
            title: 'Delete Project',
            message: 'Are you sure you want to delete this project? This action cannot be undone.',
            type: 'confirm'
        });
    };

    return (
        <>
            <Helmet>
                <title>Admin Dashboard | PyrusMedia</title>
            </Helmet>
            <div className="min-h-screen bg-black pt-32 pb-20">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center mb-12">
                        <h1 className="text-4xl font-bold text-white">Admin Dashboard</h1>
                        <button onClick={handleLogout} className="flex items-center gap-2 text-red-500 hover:text-red-400">
                            <LogOut size={20} /> Logout
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 mb-8 border-b border-gray-800 pb-4">
                        <button
                            onClick={() => setActiveTab('courses')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'courses' ? 'bg-[#bebd19] text-black' : 'text-gray-400 hover:text-white'}`}
                        >
                            Manage Courses
                        </button>
                        <button
                            onClick={() => setActiveTab('jobs')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'jobs' ? 'bg-[#bebd19] text-black' : 'text-gray-400 hover:text-white'}`}
                        >
                            Manage Jobs
                        </button>
                        <button
                            onClick={() => setActiveTab('team')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'team' ? 'bg-[#bebd19] text-black' : 'text-gray-400 hover:text-white'}`}
                        >
                            Manage Team
                        </button>
                        <button
                            onClick={() => setActiveTab('projects')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'projects' ? 'bg-[#bebd19] text-black' : 'text-gray-400 hover:text-white'}`}
                        >
                            Manage Work
                        </button>
                    </div>

                    {activeTab === 'courses' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-white mb-4">Existing Courses</h2>
                                {loading ? (
                                    <div className="text-gray-400 py-10 text-center">Loading courses...</div>
                                ) : courses.length > 0 ? (
                                    courses.map(course => (
                                        <div key={course.id} className="bg-[#111] p-6 rounded-xl border border-gray-800 flex justify-between items-center group">
                                            <div className="flex items-center gap-4">
                                                {course.thumbnail_url && (
                                                    <img src={course.thumbnail_url} alt={course.title} className="w-16 h-16 object-cover rounded-md" />
                                                )}
                                                <div>
                                                    <h3 className="text-xl font-bold text-white group-hover:text-[#bebd19] transition-colors">{course.title}</h3>
                                                    <p className="text-sm text-gray-500">{course.level} • <span>&#8377;</span> {course.price.replace(/[^0-9.]/g, '')}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditCourse(course)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteCourse(course.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-[#111] rounded-xl border border-gray-800 border-dashed">
                                        <p className="text-gray-500">No courses added yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-[#111] p-6 rounded-xl border border-gray-800 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-white mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Course' : 'Add New Course'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitCourse} className="space-y-3">
                                    <input required placeholder="Course Title" value={courseForm.title} onChange={e => setCourseForm(prev => ({ ...prev, title: e.target.value }))} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />

                                    {/* Image Upload */}
                                    <div className="space-y-2">
                                        <label className="block text-gray-400 text-sm">Thumbnail Image</label>
                                        <div className="flex gap-2 items-center">
                                            <label className="cursor-pointer bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded flex items-center gap-2 text-sm transition-colors">
                                                <Upload size={16} />
                                                {uploading ? 'Uploading...' : 'Choose Image'}
                                                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                                            </label>
                                            {courseForm.thumbnail_url && (
                                                <img src={courseForm.thumbnail_url} alt="Preview" className="h-10 w-10 object-cover rounded border border-gray-700" />
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-white text-black rounded">
                                        <ReactQuill
                                            theme="snow"
                                            value={courseForm.desc}
                                            onChange={value => setCourseForm(prev => ({ ...prev, desc: value }))}
                                            placeholder="Description"
                                            className="bg-white text-black rounded"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input required placeholder="Level (e.g. Beginner)" value={courseForm.level} onChange={e => setCourseForm(prev => ({ ...prev, level: e.target.value }))} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                        <input 
                                            required 
                                            placeholder="Price (e.g. 4999)" 
                                            value={courseForm.price} 
                                            onChange={e => {
                                                const val = e.target.value;
                                                // Allow numbers and one decimal point
                                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                    setCourseForm(prev => ({ ...prev, price: val }));
                                                }
                                            }}
                                            className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 text-xs mb-1">Batch Start Date</label>
                                        <input
                                            type="date"
                                            value={courseForm.batch_date}
                                            onChange={e => setCourseForm(prev => ({ ...prev, batch_date: e.target.value }))}
                                            className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none [color-scheme:dark]"
                                        />
                                    </div>
                                    <select value={courseForm.status} onChange={e => setCourseForm(prev => ({ ...prev, status: e.target.value }))} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none">
                                        <option value="Enroll Now">Enroll Now</option>
                                        <option value="Coming Soon">Coming Soon</option>
                                        <option value="Waitlist">Waitlist</option>
                                    </select>
                                    <Button type="submit" disabled={uploading} className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}>
                                        {uploading ? 'Please wait...' : (editingId ? 'Update Course' : 'Add Course')}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeTab === 'jobs' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-white mb-4">Open Positions</h2>
                                {loading ? (
                                    <div className="text-gray-400 py-10 text-center">Loading jobs...</div>
                                ) : jobs.length > 0 ? (
                                    jobs.map(job => (
                                        <div key={job.id} className="bg-[#111] p-6 rounded-xl border border-gray-800 flex justify-between items-center group">
                                            <div>
                                                <h3 className="text-xl font-bold text-white group-hover:text-[#bebd19] transition-colors">{job.title}</h3>
                                                <p className="text-sm text-gray-500">{job.type}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditJob(job)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteJob(job.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-[#111] rounded-xl border border-gray-800 border-dashed">
                                        <p className="text-gray-500">No active job openings.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-[#111] p-6 rounded-xl border border-gray-800 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-white mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Job' : 'Add New Job'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitJob} className="space-y-3">
                                    <input required placeholder="Job Title" value={jobForm.title} onChange={e => setJobForm({ ...jobForm, title: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    <input required placeholder="Type (e.g. Remote / Full-time)" value={jobForm.type} onChange={e => setJobForm({ ...jobForm, type: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    <textarea required rows="3" placeholder="Short Description" value={jobForm.desc} onChange={e => setJobForm({ ...jobForm, desc: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    <Button type="submit" className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}>
                                        {editingId ? 'Update Job' : 'Add Job'}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeTab === 'team' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-white mb-4">Team Members</h2>
                                {loading ? (
                                    <div className="text-gray-400 py-10 text-center">Loading team...</div>
                                ) : teamMembers.length > 0 ? (
                                    teamMembers.map(member => (
                                        <div key={member.id} className="bg-[#111] p-6 rounded-xl border border-gray-800 flex justify-between items-center group">
                                            <div className="flex items-center gap-4">
                                                {member.image_url ? (
                                                    <img src={member.image_url} alt={member.name} className="w-16 h-16 object-cover rounded-full border border-gray-800" />
                                                ) : (
                                                    <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center text-gray-500">
                                                        <Users size={24} />
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 className="text-xl font-bold text-white group-hover:text-[#bebd19] transition-colors">{member.name}</h3>
                                                    <p className="text-sm text-gray-500">{member.role}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditTeamMember(member)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteTeamMember(member.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-[#111] rounded-xl border border-gray-800 border-dashed">
                                        <p className="text-gray-500">No team members added yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-[#111] p-6 rounded-xl border border-gray-800 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-white mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Member' : 'Add Team Member'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitTeam} className="space-y-3">
                                    <input required placeholder="Name" value={teamForm.name} onChange={e => setTeamForm({ ...teamForm, name: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    <input required placeholder="Role (e.g. CEO, Developer)" value={teamForm.role} onChange={e => setTeamForm({ ...teamForm, role: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    
                                    {/* Image Upload */}
                                    <div className="space-y-2">
                                        <label className="block text-gray-400 text-sm">Profile Photo</label>
                                        <div className="flex gap-2 items-center">
                                            <label className="cursor-pointer bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded flex items-center gap-2 text-sm transition-colors">
                                                <Upload size={16} />
                                                {uploading ? 'Uploading...' : 'Choose Image'}
                                                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'team')} className="hidden" disabled={uploading} />
                                            </label>
                                            
                                            {teamForm.image_url && (
                                                <div className="flex items-center gap-2">
                                                    <img src={teamForm.image_url} alt="Preview" className="h-10 w-10 object-cover rounded-full border border-gray-700" />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setTeamForm(prev => ({ ...prev, image_url: '' }))}
                                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-500 p-2 rounded transition-colors"
                                                        title="Remove Image"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <Button type="submit" className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}>
                                        {editingId ? 'Update Member' : 'Add Member'}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}


                    {activeTab === 'projects' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-white mb-4">Our Work / Projects</h2>
                                {loading ? (
                                    <div className="text-gray-400 py-10 text-center">Loading projects...</div>
                                ) : projects.length > 0 ? (
                                    projects.map(project => (
                                        <div key={project.id} className="bg-[#111] p-6 rounded-xl border border-gray-800 flex justify-between items-center group">
                                            <div className="flex items-center gap-4">
                                                {project.image_url ? (
                                                    <img src={project.image_url} alt={project.title} className="w-24 h-16 object-cover rounded-md border border-gray-800" />
                                                ) : (
                                                    <div className="w-24 h-16 rounded-md bg-gray-800 flex items-center justify-center text-gray-500 text-xs">
                                                        No Image
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 className="text-xl font-bold text-white group-hover:text-[#bebd19] transition-colors">{project.title}</h3>
                                                    <p className="text-sm text-gray-500">{project.category}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditProject(project)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteProject(project.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-[#111] rounded-xl border border-gray-800 border-dashed">
                                        <p className="text-gray-500">No projects added yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-[#111] p-6 rounded-xl border border-gray-800 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-white mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Project' : 'Add New Project'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitProject} className="space-y-3">
                                    <input required placeholder="Project Title" value={projectForm.title} onChange={e => setProjectForm({ ...projectForm, title: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    <input required placeholder="Category (e.g. Web App, Mobile App)" value={projectForm.category} onChange={e => setProjectForm({ ...projectForm, category: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    
                                    {/* Image Upload */}
                                    <div className="space-y-2">
                                        <label className="block text-gray-400 text-sm">Project Image</label>
                                        <div className="flex gap-2 items-center">
                                            <label className="cursor-pointer bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded flex items-center gap-2 text-sm transition-colors">
                                                <Upload size={16} />
                                                {uploading ? 'Uploading...' : 'Choose Image'}
                                                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'project')} className="hidden" disabled={uploading} />
                                            </label>
                                            
                                            {projectForm.image_url && (
                                                <div className="flex items-center gap-2">
                                                    <img src={projectForm.image_url} alt="Preview" className="h-10 w-10 object-cover rounded border border-gray-700" />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setProjectForm(prev => ({ ...prev, image_url: '' }))}
                                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-500 p-2 rounded transition-colors"
                                                        title="Remove Image"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <textarea required rows="4" placeholder="Description" value={projectForm.description} onChange={e => setProjectForm({ ...projectForm, description: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />
                                    <input placeholder="Project Link (Optional)" value={projectForm.project_link} onChange={e => setProjectForm({ ...projectForm, project_link: e.target.value })} className="w-full bg-black border border-gray-800 rounded p-2 text-white focus:border-[#bebd19] outline-none" />

                                    <Button type="submit" className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}>
                                        {editingId ? 'Update Project' : 'Add Project'}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <CustomDialog
                isOpen={dialog.isOpen}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                onClose={closeDialog}
                onConfirm={handleConfirmAction}
                confirmText="Delete"
            />
        </>
    );
};

export default AdminDashboard;
